/**
 * Maps batch-service v2 / dashboard BFF shapes to the UI model used by
 * batch-service (camelCase + legacy field names).
 */

import axios from "axios";
import { batchPaths } from "../../../api/batch";
import { authorizationHeaders } from "../../../utils/authSession";
import { mapCustomerPath } from "../../../utils/pathMap";

/** Dashboard BFF / batch-service v2 → user-visible error (legacy FileReadError text). */
export function extractBatchApiErrorMessage(
  err: unknown,
  fallback: string
): string {
  if (err && typeof err === "object" && !("response" in (err as object))) {
    const direct = err as Record<string, unknown>;
    const nested = direct.error as Record<string, unknown> | undefined;
    if (typeof nested?.message === "string" && nested.message) {
      return nested.message;
    }
    if (typeof direct.message === "string" && direct.message) {
      return direct.message;
    }
    if (typeof direct.err === "string" && direct.err) {
      return direct.err;
    }
  }

  const e = err as {
    response?: { data?: Record<string, unknown> | string };
    message?: string;
  };
  const data = e?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  if (data && typeof data === "object") {
    const errObj = data.error as Record<string, unknown> | undefined;
    if (typeof errObj?.message === "string" && errObj.message) {
      return errObj.message;
    }
    if (typeof data.message === "string" && data.message) return data.message;
    if (typeof data.err === "string" && data.err) return data.err;
  }
  if (typeof e?.message === "string" && e.message) return e.message;
  return fallback;
}

export function extractBatchTypesPayload(res: any): any[] {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  return [];
}

export function extractTaskListPayload(res: any): any[] {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  return [];
}

/** Total count from dashboard BFF / batch-service TaskListResult (null if absent). */
export function extractTaskListTotal(res: any): number | null {
  if (!res || typeof res !== "object" || Array.isArray(res)) return null;
  if (typeof res.total === "number") return res.total;
  if (typeof res?.metadata?.total === "number") return res.metadata.total;
  if (typeof res?.metadata === "string" && res.metadata.trim()) {
    try {
      const meta = JSON.parse(res.metadata);
      if (typeof meta?.total === "number") return meta.total;
    } catch {
      // ignore malformed metadata
    }
  }
  return null;
}

/** Upstream default when `limit` is omitted on GET /batch/tasks. */
export const BATCH_TASKS_DEFAULT_PAGE_SIZE = 20;

/** Query string for GET /batch/tasks (`limit` omitted → upstream default page size). */
export function batchTasksListSearchParams(
  page: number,
  filters: Record<string, string | undefined> = {},
  limit?: number
): URLSearchParams {
  const params = new URLSearchParams();
  params.set("page", String(page));
  if (typeof limit === "number" && limit > 0) {
    params.set("limit", String(limit));
  }
  for (const [key, value] of Object.entries(filters)) {
    if (value != null && String(value).trim() !== "") {
      params.set(key, String(value));
    }
  }
  return params;
}

/** True when more upstream pages exist after appending `lastPageRows`. */
export function batchTasksHasMore(
  loadedCount: number,
  total: number | null,
  lastPageRows: number,
  assumedPageSize = BATCH_TASKS_DEFAULT_PAGE_SIZE
): boolean {
  if (lastPageRows === 0) return false;
  if (typeof total === "number") {
    return loadedCount < total;
  }
  return lastPageRows >= assumedPageSize;
}

/**
 * Maps customer UI status labels to batch-service batch_state query values.
 * "Waiting for approval" and "ERROR" are UI-only labels — no single API state.
 */
export function mapStatusFilterToBatchState(value?: string): string | undefined {
  const v = String(value ?? "").trim();
  if (!v || v === "Waiting for approval" || v === "ERROR") {
    return undefined;
  }
  return v.toLowerCase();
}

/** ISO 8601 timestamps for list API (admin portal parity; end-of-day for `to`). */
export function formatListDateISO(
  dateInput?: string | Date,
  isEndOfDay = false
): string | null {
  if (!dateInput) return null;
  const date =
    dateInput instanceof Date ? new Date(dateInput) : new Date(dateInput);
  if (Number.isNaN(date.getTime())) return null;

  // Use LOCAL day boundaries (not UTC) so a picked calendar date maps to the
  // user's local day. toISOString() then converts to the correct UTC instant.
  // Using setUTCHours here shifts the window by the local TZ offset (e.g. IST
  // +5:30 made a 22->23 filter surface next-day batches).
  if (isEndOfDay) {
    date.setHours(23, 59, 59, 999);
  } else {
    date.setHours(0, 0, 0, 0);
  }
  return date.toISOString();
}

/** Client-side filter for UI-only status labels after a server-side list fetch. */
export function matchesUiStatusFilter(item: any, uiStatus?: string): boolean {
  const v = String(uiStatus ?? "").trim();
  if (!v) return true;
  const state = String(item?.batchState ?? "").trim();
  if (v === "Waiting for approval") {
    return state === "Waiting for approval";
  }
  if (v === "ERROR") {
    return state === "ERROR";
  }
  return state === v;
}

/**
 * batch-service v2 uses lowercase states (e.g. "finished", "running").
 * Customer UI expects uppercase labels used by filters and progress cards.
 */
export function normalizeBatchStateForUi(raw: string | undefined | null): string {
  const key = String(raw ?? "")
    .trim()
    .toLowerCase();
  switch (key) {
    case "registered":
    case "initialized":
    case "created":
      return "Waiting for approval";
    case "finished":
      return "FINISHED";
    case "running":
      return "RUNNING";
    case "paused":
      return "PAUSED";
    case "stopped":
      return "STOPPED";
    case "error":
      return "ERROR";
    default:
      if (!key) return "";
      return String(raw).toUpperCase();
  }
}

function parseResponseCodeCount(raw: unknown): any[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

export function normalizeBatchListRow(row: any) {
  const batchState = normalizeBatchStateForUi(
    row.batch_state ?? row.batchState ?? ""
  );

  const downloadAvailable =
    row.download_available === true ||
    row.download_available === "true" ||
    row.downloadAvailable === true;

  const totalItems = row.total_items ?? row.totalItems ?? 0;
  const totalProcessed = row.total_processed ?? row.totalProcessed ?? 0;

  return {
    ...row,
    batchId: row.batch_id ?? row.batchId,
    batchName: row.batch_name ?? row.batchName,
    batchType: row.product_arn ?? row.batchType,
    batchState,
    orgId: row.org_id ?? row.orgId,
    orgName: row.org_name ?? row.orgName,
    totalItems,
    totalProcessed,
    created_at: row.created_at,
    updated_at:
      row.updated_at ??
      row.batch_finished_at ??
      row.batch_started_at ??
      row.created_at,
    batch_source: row.batch_source ?? "v2",
    tpm: row.tpm,
    downloadUrl: row.download_url ?? row.downloadUrl ?? null,
    downloadInitiated: row.download_initiated ?? row.downloadInitiated ?? false,
    downloadAvailable,
    resultsFilePid: row.results_file_pid ?? row.resultsFilePid ?? "",
    successCount: parseResponseCodeCount(
      row.response_code_count ?? row.successCount
    ),
  };
}

export function extractResultsFileUrl(res: any): string | null {
  return extractBatchResultsDownload(res).fileUrl;
}

export function batchResultsDownloadFileName(
  batchName: string | null | undefined,
  batchId: string | null | undefined,
  filePid: string | null | undefined
): string {
  const sanitize = (part: string) =>
    part
      .replace(/[/\\:?*"<>|\r\n\x00]+/g, "_")
      .replace(/^[.\s_]+/, "")
      .replace(/[.\s_]+$/, "");

  const name = sanitize(String(batchName ?? "").trim()) || "batch";
  const id = sanitize(String(batchId ?? "").trim()) || "batch";
  const pid = sanitize(String(filePid ?? "").trim());
  if (!pid) return `${name}_${id}.zip`;
  return `${name}_${id}_${pid}.zip`;
}

export function extractBatchResultsDownload(res: any): {
  fileUrl: string | null;
  filePid: string | null;
  fileName: string | null;
} {
  if (!res) return { fileUrl: null, filePid: null, fileName: null };
  const inner = res.data !== undefined ? res.data : res;
  const fileUrl =
    typeof inner?.file_url === "string" && inner.file_url.length > 0
      ? inner.file_url
      : typeof res?.file_url === "string" && res.file_url.length > 0
        ? res.file_url
        : null;
  const filePid =
    typeof inner?.file_pid === "string" && inner.file_pid.trim()
      ? inner.file_pid.trim()
      : typeof res?.file_pid === "string" && res.file_pid.trim()
        ? res.file_pid.trim()
        : null;
  const fileName =
    typeof inner?.file_name === "string" && inner.file_name.trim()
      ? inner.file_name.trim()
      : typeof res?.file_name === "string" && res.file_name.trim()
        ? res.file_name.trim()
        : null;
  return { fileUrl, filePid, fileName };
}

/** Open a signed storage URL the same way as sample CSV (no new tab). */
function openSignedDownloadUrl(fileUrl: string): void {
  const anchor = document.createElement("a");
  anchor.href = fileUrl;
  anchor.setAttribute("download", "");
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}

function extractFileUrlFromGenerateLinkResponse(data: unknown): string {
  if (!data || typeof data !== "object") return "";
  const root = data as Record<string, unknown>;
  const inner =
    root.data && typeof root.data === "object"
      ? (root.data as Record<string, unknown>)
      : root;
  const fromInner =
    typeof inner.file_url === "string" ? inner.file_url.trim() : "";
  if (fromInner) return fromInner;
  return typeof root.file_url === "string" ? root.file_url.trim() : "";
}

/**
 * Download batch results via signed URL only (no BFF zip proxy / blob buffer).
 * Same approach as sample CSV: mint or reuse file_url, then open it in the browser.
 */
export async function downloadBatchResultsViaBff(
  batchId: string,
  _fallbackFileName?: string | null,
  options?: { downloadUrl?: string | null; batchSource?: string | null }
): Promise<void> {
  const id = String(batchId ?? "").trim();
  if (!id) {
    throw new Error("batchId is required");
  }

  // Legacy list rows often already carry a signed download_url.
  let fileUrl = String(options?.downloadUrl ?? "").trim();

  if (!fileUrl) {
    const base = (process.env.REACT_APP_SERVER_URI || "").replace(/\/$/, "");
    const url = `${base}${mapCustomerPath(batchPaths.generateDownloadLink(id))}`;
    const serviceId = (process.env.REACT_APP_SERVICE_ID || "").trim();
    const response = await axios.post(url, null, {
      timeout: 60 * 1000,
      validateStatus: () => true,
      withCredentials: true,
      headers: {
        Accept: "application/json",
        ...authorizationHeaders(),
        ...(serviceId && { "x-service-id": serviceId }),
      },
    });

    if (response.status < 200 || response.status >= 300) {
      throw new Error(
        extractBatchApiErrorMessage(
          { response },
          `Failed to get download link (HTTP ${response.status})`
        )
      );
    }

    fileUrl = extractFileUrlFromGenerateLinkResponse(response.data);
    if (!fileUrl) {
      throw new Error("No results file URL for this batch");
    }
  }

  openSignedDownloadUrl(fileUrl);
}
